import { commitLocalUpdate } from 'react-relay';

import { graphql } from 'babel-plugin-relay/macro';
import { get, isArray } from 'lodash';
import { call, select, takeLatest } from 'redux-saga/effects';
import { fetchQuery } from 'relay-runtime';
import { createAsyncAction, getType } from 'typesafe-actions';
import { LoggerStatusEnum, sendLog } from 'ui-model';

import { selectAuthUser } from '@lib/selectors';
import environment from '@lib/utils/RelayEnvironment';

import { ON_SOCKET_PRESENCE_USER_OFFLINE, ON_SOCKET_PRESENCE_USER_ONLINE } from 'containers/App/constants';
import { DELETE_USER_SUCCESS } from 'containers/DialogDeleteUser/constants';
import { toGlobalId } from 'services/relayService';

import sagaRelayInvalidateData from './sagaRelayInvalidateData';
import { sagaWatcher } from './sagaWatchersViewer';

export const actionUpdateUser = createAsyncAction(
  'actionUpdateUser/REQUEST',
  'actionUpdateUser/SUCCESS',
  'actionUpdateUser/FAILURE',
)<any, any, any>();

export const actionResetPassword = createAsyncAction(
  'actionResetPassword/REQUEST',
  'actionResetPassword/SUCCESS',
  'actionResetPassword/FAILURE',
)<any, any, any>();

export const actionAddUser = createAsyncAction(
  'actionAddUser/REQUEST',
  'actionAddUser/SUCCESS',
  'actionAddUser/FAILURE',
)<any, any, any>();

const query = graphql`
  query sagaWatchersUserQuery($id: MongoID!) {
    userById(_id: $id) {
      name
      firstname
      lastname
      oid_avatar
      displayPhone
      displayStatus
      active
      deleted
      oid_groups
      roles
      salesforceData {
        provisioning
      }
    }
  }
`;

const queryIds = graphql`
  query sagaWatchersUser_ArrayIdQuery($ids: [MongoID!]!) {
    userByIds(_ids: $ids) {
      name
      firstname
      lastname
      oid_avatar
      displayPhone
      displayStatus
      active
      deleted
      oid_groups
      roles
      salesforceData {
        provisioning
      }
    }
  }
`;

function* sagaInvalidateStore() {
  yield sagaRelayInvalidateData('User');
}

function* sagaWatcherUpdateUser(data: any) {
  try {
    yield sagaRelayInvalidateData('User');

    const userId =
      get(data, '_id', get(data, 'payload.data._id', get(data, 'payload.requestPayload._id', ''))) ||
      get(data, 'payload.data.updated');

    const viewerId: string = yield select(selectAuthUser);
    if (userId) {
      if (userId === viewerId) {
        yield sagaWatcher(data);
      } else if (isArray(userId)) {
        yield call(fetchQuery, environment, queryIds, { ids: userId });
      } else {
        yield call(fetchQuery, environment, query, { id: userId });
      }
    } else {
      sendLog('sagaWatchersUser#sagaWatcherUpdateUser _id not found', { data }, LoggerStatusEnum.error);
    }
  } catch (error) {
    sendLog('sagaWatchersUser#sagaWatcherUpdateUser', { error }, LoggerStatusEnum.error);
  }
}

// eslint-disable-next-line require-yield
function* sagaWatcherOnline(action: any) {
  try {
    const _id = get(action, 'data.user');
    if (!_id) {
      sendLog('sagaWatcherOnline _id not found', { action }, LoggerStatusEnum.warn);
      return;
    }

    commitLocalUpdate(environment, (store) => {
      const userRecord =
        store.get(toGlobalId('User', _id)) ||
        store.get(toGlobalId('Provider', _id)) ||
        store.get(toGlobalId('Patient', _id));
      if (!userRecord) {
        return;
      }
      const isOnline = action.type === ON_SOCKET_PRESENCE_USER_ONLINE;
      userRecord.setValue(isOnline, 'isOnline');
    });
  } catch (error) {
    sendLog('sagaWatcherOnline', { error, action }, LoggerStatusEnum.error);
  }
}

export default function* sagaWatchersUser() {
  // Invalidate data on Relay store and refetch queries
  yield takeLatest(DELETE_USER_SUCCESS, sagaInvalidateStore);
  yield takeLatest(getType(actionAddUser.success), sagaInvalidateStore);
  yield takeLatest(ON_SOCKET_PRESENCE_USER_ONLINE, sagaWatcherOnline);
  yield takeLatest(ON_SOCKET_PRESENCE_USER_OFFLINE, sagaWatcherOnline);
  yield takeLatest(getType(actionUpdateUser.success), sagaWatcherUpdateUser);
}
